import { useEffect, useMemo, useState } from "react";
import { api } from "../../utils/api.js";
import { formatDateTime } from "../../utils/date.js";
import {
  INTERVIEW_SCORES,
  INTERVIEW_SCORE_LABELS,
  INTERVIEW_SCORE_COLORS,
  INTERVIEW_SCORE_DESCRIPTIONS,
  PANEL_STATUS_LABELS,
  PANEL_STATUS_COLORS,
  type InterviewScore,
  type PanelStatus,
} from "../../utils/interview.js";

export interface PanelInterviewer {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  submittedAt: string | null;
  isMe: boolean;
  score: InterviewScore | null;
  scoreLabel: string | null;
  notes: string | null;
  hasHiddenFeedback: boolean;
}

export interface PanelData {
  id: number;
  candidateId: number;
  createdById: number;
  createdByName: string | null;
  createdAt: string;
  status: PanelStatus;
  totalInterviewers: number;
  submittedCount: number;
  allFeedbackIn: boolean;
  interviewers: PanelInterviewer[];
  finalScore: InterviewScore | null;
  finalScoreLabel: string | null;
  finalComments: string | null;
  finalSubmittedAt: string | null;
  finalSubmittedById: number | null;
  finalSubmittedByName: string | null;
  canManage: boolean;
  canSubmitFinal: boolean;
  canSubmitFeedback: boolean;
  isPanelist: boolean;
}

interface EmployeeUser {
  id: number;
  email: string;
  displayName: string;
  role: string;
}

interface Props {
  candidateId: number;
  candidateName: string;
  hiringManagerEmail: string | null;
  currentUserEmail: string;
  panel: PanelData | null;
  /** Whether the viewer can create a panel (HM/admin and stage is "interview"). */
  canCreatePanel: boolean;
  onChange: () => void;
}

export function InterviewPanel({
  candidateId,
  candidateName,
  hiringManagerEmail,
  currentUserEmail,
  panel,
  canCreatePanel,
  onChange,
}: Props) {
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [finalOpen, setFinalOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-open the feedback form if viewer is a panelist who hasn't submitted yet
  const mySubmission = useMemo(() => panel?.interviewers.find((i) => i.isMe) ?? null, [panel]);

  if (!panel) {
    return (
      <PanelSection>
        {canCreatePanel ? (
          <div className="space-y-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Interview Panel</h3>
              <p className="text-sm text-gray-500">
                Assemble a panel of interviewers to collect structured feedback. Each interviewer
                will receive an email invitation with a link to submit their feedback.
              </p>
            </div>
            <button
              onClick={() => setCreatorOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] transition-colors"
            >
              <PlusIcon />
              Create Interview Panel
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900">Interview Panel</h3>
            <p className="text-sm text-gray-500">
              No panel has been assembled yet. The hiring manager
              {hiringManagerEmail ? ` (${hiringManagerEmail})` : ""} can create one once the
              candidate is in the interview stage.
            </p>
          </div>
        )}
        {creatorOpen && (
          <PanelEditorModal
            candidateId={candidateId}
            candidateName={candidateName}
            currentInterviewerIds={[]}
            currentUserEmail={currentUserEmail}
            mode="create"
            onClose={() => setCreatorOpen(false)}
            onSuccess={() => {
              setCreatorOpen(false);
              onChange();
            }}
          />
        )}
      </PanelSection>
    );
  }

  return (
    <PanelSection>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Interview Panel</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Created {formatDateTime(panel.createdAt)}
              {panel.createdByName ? ` by ${panel.createdByName}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${PANEL_STATUS_COLORS[panel.status]}`}
            >
              {panel.status === "collecting" && (
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
              )}
              {PANEL_STATUS_LABELS[panel.status]}
              {panel.status === "collecting" && panel.totalInterviewers > 0 && (
                <span className="text-amber-700/70">
                  {panel.submittedCount} / {panel.totalInterviewers}
                </span>
              )}
            </span>
            {panel.canManage && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditorOpen(true)}
                  className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Edit Panel
                </button>
                <button
                  onClick={() => setDeleteOpen(true)}
                  className="rounded-md border border-red-200 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Final decision banner */}
        {panel.finalScore && (
          <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-teal-700">
                  Final Decision
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-sm font-semibold ${INTERVIEW_SCORE_COLORS[panel.finalScore]}`}
                  >
                    {INTERVIEW_SCORE_LABELS[panel.finalScore]}
                  </span>
                </div>
              </div>
              <div className="text-xs text-teal-700 text-right">
                {panel.finalSubmittedByName && (
                  <div className="font-medium">by {panel.finalSubmittedByName}</div>
                )}
                {panel.finalSubmittedAt && <div>{formatDateTime(panel.finalSubmittedAt)}</div>}
              </div>
            </div>
            {panel.finalComments && (
              <p className="mt-3 text-sm text-teal-900 whitespace-pre-wrap border-t border-teal-200 pt-3">
                {panel.finalComments}
              </p>
            )}
          </div>
        )}

        {/* Interviewers list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium uppercase tracking-wider text-gray-500">
              Interviewers
            </h4>
            {panel.canSubmitFinal && (
              <button
                onClick={() => setFinalOpen(true)}
                className="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 transition-colors"
              >
                Submit Final Decision
              </button>
            )}
          </div>
          <div className="space-y-2">
            {panel.interviewers.map((interviewer) => (
              <InterviewerCard
                key={interviewer.id}
                interviewer={interviewer}
                canEdit={interviewer.isMe && panel.canSubmitFeedback}
                onEdit={() => setFeedbackOpen(true)}
              />
            ))}
          </div>
        </div>

        {/* My feedback CTA */}
        {panel.canSubmitFeedback && !mySubmission?.submittedAt && (
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-sm font-semibold text-primary-900">Your feedback is needed</p>
                <p className="text-xs text-primary-700 mt-0.5">
                  Submit your score and interview notes so the panel can move forward.
                </p>
              </div>
              <button
                onClick={() => setFeedbackOpen(true)}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] transition-colors"
              >
                Submit Feedback
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
      </div>

      {editorOpen && (
        <PanelEditorModal
          candidateId={candidateId}
          candidateName={candidateName}
          currentInterviewerIds={panel.interviewers.map((i) => i.userId)}
          currentUserEmail={currentUserEmail}
          mode="edit"
          onClose={() => setEditorOpen(false)}
          onSuccess={() => {
            setEditorOpen(false);
            onChange();
          }}
        />
      )}
      {feedbackOpen && mySubmission && (
        <FeedbackModal
          candidateId={candidateId}
          interviewerName={mySubmission.userName}
          initialScore={mySubmission.score}
          initialNotes={mySubmission.notes ?? ""}
          onClose={() => setFeedbackOpen(false)}
          onSuccess={() => {
            setFeedbackOpen(false);
            onChange();
          }}
        />
      )}
      {finalOpen && (
        <FinalDecisionModal
          candidateId={candidateId}
          candidateName={candidateName}
          interviewers={panel.interviewers}
          onClose={() => setFinalOpen(false)}
          onSuccess={() => {
            setFinalOpen(false);
            onChange();
          }}
        />
      )}
      {deleteOpen && (
        <DeletePanelModal
          candidateId={candidateId}
          onClose={() => setDeleteOpen(false)}
          onSuccess={() => {
            setDeleteOpen(false);
            onChange();
          }}
          onError={setError}
        />
      )}
    </PanelSection>
  );
}

function PanelSection({ children }: { children: React.ReactNode }) {
  return <div className="rounded-lg border border-gray-200 bg-white p-5">{children}</div>;
}

function InterviewerCard({
  interviewer,
  canEdit,
  onEdit,
}: {
  interviewer: PanelInterviewer;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const submitted = interviewer.submittedAt != null;
  return (
    <div
      className={`rounded-lg border p-3 ${interviewer.isMe ? "border-primary-200 bg-primary-50/40" : "border-gray-200 bg-white"}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-700">
            {initials(interviewer.userName)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-medium text-gray-900">
                {interviewer.userName}
              </span>
              {interviewer.isMe && (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-primary-700">
                  You
                </span>
              )}
            </div>
            <div className="truncate text-xs text-gray-500">{interviewer.userEmail}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {submitted ? (
            interviewer.score ? (
              <span
                className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${INTERVIEW_SCORE_COLORS[interviewer.score]}`}
              >
                {INTERVIEW_SCORE_LABELS[interviewer.score]}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                <LockIcon />
                Submitted
              </span>
            )
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400" />
              Pending
            </span>
          )}
          {canEdit && submitted && (
            <button
              onClick={onEdit}
              className="text-xs text-primary-700 hover:text-primary-800 font-medium"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      {/* Notes section */}
      {interviewer.notes != null && interviewer.notes !== "" && (
        <p className="mt-3 text-sm text-gray-700 whitespace-pre-wrap border-t border-gray-200/60 pt-3">
          {interviewer.notes}
        </p>
      )}
      {interviewer.hasHiddenFeedback && <HiddenFeedbackBlob />}
      {submitted && interviewer.submittedAt && !interviewer.isMe && (
        <p className="mt-2 text-[11px] text-gray-400">
          Submitted {formatDateTime(interviewer.submittedAt)}
        </p>
      )}
    </div>
  );
}

function HiddenFeedbackBlob() {
  return (
    <div className="mt-3 space-y-2 border-t border-gray-200/60 pt-3">
      <div className="space-y-1.5 blur-sm select-none pointer-events-none">
        <div className="h-2.5 w-[92%] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse" />
        <div className="h-2.5 w-[78%] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse [animation-delay:120ms]" />
        <div className="h-2.5 w-[64%] rounded bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse [animation-delay:240ms]" />
      </div>
      <p className="flex items-center gap-1 text-[11px] italic text-gray-400">
        <LockIcon />
        Revealed once all interviewers submit
      </p>
    </div>
  );
}

function PanelEditorModal({
  candidateId,
  candidateName,
  currentInterviewerIds,
  currentUserEmail,
  mode,
  onClose,
  onSuccess,
}: {
  candidateId: number;
  candidateName: string;
  currentInterviewerIds: number[];
  currentUserEmail: string;
  mode: "create" | "edit";
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [employees, setEmployees] = useState<EmployeeUser[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set(currentInterviewerIds));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api
      .get<{ results: EmployeeUser[] }>("/users?role=employee")
      .then((d) => setEmployees(d.results))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load employees"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter(
      (u) => u.displayName.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
    );
  }, [employees, query]);

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { interviewerIds: Array.from(selected) };
      if (mode === "create") {
        await api.post(`/candidates/${candidateId}/interview-panel`, payload);
      } else {
        await api.put(`/candidates/${candidateId}/interview-panel`, payload);
      }
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save panel");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-semibold text-gray-900">
        {mode === "create" ? "Create interview panel" : "Edit interview panel"}
      </h3>
      <p className="mt-1 text-sm text-gray-600">
        Select interviewers for <span className="font-medium">{candidateName}</span>. Each
        interviewer will get an email invitation the first time they're added.
      </p>

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by name or email..."
        className="mt-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        autoFocus
      />

      <div className="mt-2 max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {loading ? (
          <div className="p-4 text-sm text-gray-400 animate-pulse">Loading employees...</div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No employees match.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((u) => {
              const checked = selected.has(u.id);
              const isSelf = u.email.toLowerCase() === currentUserEmail.toLowerCase();
              return (
                <li key={u.id}>
                  <label className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(u.id)}
                      className="rounded border-gray-300 text-primary-700 focus:ring-primary-500"
                    />
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-[10px] font-semibold text-gray-700">
                      {initials(u.displayName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium text-gray-900">{u.displayName}</span>
                        {isSelf && (
                          <span className="text-[10px] uppercase tracking-wider font-semibold text-primary-700">
                            You
                          </span>
                        )}
                      </div>
                      <div className="truncate text-xs text-gray-500">{u.email}</div>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-3 text-xs text-gray-500">
        {selected.size} interviewer{selected.size === 1 ? "" : "s"} selected
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || selected.size === 0}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving..." : mode === "create" ? "Create Panel" : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
}

function FeedbackModal({
  candidateId,
  interviewerName,
  initialScore,
  initialNotes,
  onClose,
  onSuccess,
}: {
  candidateId: number;
  interviewerName: string;
  initialScore: InterviewScore | null;
  initialNotes: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [score, setScore] = useState<InterviewScore | null>(initialScore);
  const [notes, setNotes] = useState(initialNotes);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!score || !notes.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/candidates/${candidateId}/interview-panel/feedback`, {
        score,
        notes: notes.trim(),
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit feedback");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} wide>
      <h3 className="text-lg font-semibold text-gray-900">Your interview feedback</h3>
      <p className="mt-1 text-sm text-gray-600">
        Submitting as <span className="font-medium">{interviewerName}</span>. Other panelists won't
        see your score or notes until everyone has submitted.
      </p>

      <div className="mt-5 space-y-2">
        <label className="block text-sm font-medium text-gray-700">Score</label>
        <ScoreSelector value={score} onChange={setScore} />
      </div>

      <div className="mt-5 space-y-2">
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
          Interview notes <span className="text-red-500">*</span>
        </label>
        <textarea
          id="notes"
          rows={10}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Summarize what you asked, how the candidate performed, strengths, concerns, and anything else the panel should consider..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || !score || !notes.trim()}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-[var(--action-primary-hover)] disabled:opacity-50 transition-colors"
        >
          {saving ? "Submitting..." : "Submit Feedback"}
        </button>
      </div>
    </Modal>
  );
}

function FinalDecisionModal({
  candidateId,
  candidateName,
  interviewers,
  onClose,
  onSuccess,
}: {
  candidateId: number;
  candidateName: string;
  interviewers: PanelInterviewer[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [score, setScore] = useState<InterviewScore | null>(null);
  const [comments, setComments] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!score) return;
    setSaving(true);
    setError(null);
    try {
      await api.post(`/candidates/${candidateId}/interview-panel/final`, {
        score,
        comments: comments.trim(),
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit final decision");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} wide>
      <h3 className="text-lg font-semibold text-gray-900">Submit final decision</h3>
      <p className="mt-1 text-sm text-gray-600">
        Record the panel's final verdict for <span className="font-medium">{candidateName}</span>.
        Once submitted, the candidate moves to the{" "}
        <span className="font-medium">Interview Complete</span> stage and this final score appears
        on their profile and in the list.
      </p>

      {/* Summary of interviewer scores */}
      <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-1.5">
        <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
          Panel feedback summary
        </p>
        <div className="flex flex-wrap gap-1.5">
          {interviewers.map((i) =>
            i.score ? (
              <span
                key={i.id}
                title={`${i.userName}: ${INTERVIEW_SCORE_LABELS[i.score]}`}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${INTERVIEW_SCORE_COLORS[i.score]}`}
              >
                <span className="max-w-[120px] truncate">{i.userName}</span>
                <span className="font-semibold">{INTERVIEW_SCORE_LABELS[i.score]}</span>
              </span>
            ) : (
              <span
                key={i.id}
                className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-gray-600"
              >
                <LockIcon />
                {i.userName} (hidden)
              </span>
            ),
          )}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Final score{" "}
          <span className="text-xs font-normal text-gray-500">
            (you can override the panel's consensus)
          </span>
        </label>
        <ScoreSelector value={score} onChange={setScore} />
      </div>

      <div className="mt-5 space-y-2">
        <label htmlFor="final-comments" className="block text-sm font-medium text-gray-700">
          Additional comments <span className="text-xs font-normal text-gray-500">(optional)</span>
        </label>
        <textarea
          id="final-comments"
          rows={5}
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Any context, caveats, or rationale for the panel..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
        />
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <button
          onClick={onClose}
          disabled={saving}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving || !score}
          className="rounded-md bg-gray-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50 transition-colors"
        >
          {saving ? "Submitting..." : "Submit Final Decision"}
        </button>
      </div>
    </Modal>
  );
}

function DeletePanelModal({
  candidateId,
  onClose,
  onSuccess,
  onError,
}: {
  candidateId: number;
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/candidates/${candidateId}/interview-panel`);
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete panel");
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <h3 className="text-lg font-semibold text-gray-900">Delete interview panel?</h3>
      <p className="mt-2 text-sm text-gray-600">
        All panel members and their submitted feedback will be permanently removed. This cannot be
        undone.
      </p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onClose}
          disabled={deleting}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {deleting ? "Deleting..." : "Delete Panel"}
        </button>
      </div>
    </Modal>
  );
}

function ScoreSelector({
  value,
  onChange,
}: {
  value: InterviewScore | null;
  onChange: (v: InterviewScore) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {INTERVIEW_SCORES.map((score) => {
        const active = value === score;
        return (
          <button
            key={score}
            type="button"
            onClick={() => onChange(score)}
            className={`rounded-lg border px-3 py-2.5 text-left transition-all ${
              active
                ? "border-primary-500 bg-primary-50 ring-2 ring-primary-200"
                : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
            }`}
          >
            <div className="flex items-center gap-2">
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${INTERVIEW_SCORE_COLORS[score]}`}
              >
                {INTERVIEW_SCORE_LABELS[score]}
              </span>
              {active && (
                <svg
                  className="h-4 w-4 text-primary-700"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.704 5.29a1 1 0 010 1.42l-8 8a1 1 0 01-1.415 0l-4-4a1 1 0 111.415-1.415l3.293 3.293 7.293-7.293a1 1 0 011.414-.005z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">{INTERVIEW_SCORE_DESCRIPTIONS[score]}</p>
          </button>
        );
      })}
    </div>
  );
}

function Modal({
  children,
  onClose,
  wide,
}: {
  children: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className={`mx-4 w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M10 1a4 4 0 00-4 4v3H5a2 2 0 00-2 2v7a2 2 0 002 2h10a2 2 0 002-2v-7a2 2 0 00-2-2h-1V5a4 4 0 00-4-4zm2 7V5a2 2 0 10-4 0v3h4z"
        clipRule="evenodd"
      />
    </svg>
  );
}
